import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { AlertUnsavedData, Modal, ModalSize, NotificationToaster } from "components/notifications";
import { FormTextInput } from "components/form-fields";
import { Button, ButtonSave } from "components/buttons";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PcnService } from "services/PcnService";
import { Intent } from "@blueprintjs/core";

const issuingAuthorityCodeMaxLength = 10;
const nameMaxLength = 100;
const websiteMaxLength = 255;
const badRequestCode = 400;

const defaultModel = {
    id: 0,
    issuingAuthorityCode: "",
    name: "",
    appealWebsite: "",
    paymentWebsite: "",
    detailsWebsite: "",
    homeWebsite: ""
};

export function PcnIssuingAuthorityModal(
    {
        showModal,
        modalTitle,
        isUpdate = false,
        loading = false,
        onSave,
        onCloseModal,
        issuingAuthorityDetails = defaultModel
    }
) {
    const [original, setOriginal] = useState(defaultModel);
    const [current, setCurrent] = useState(defaultModel);
    const [saving, setSaving] = useState(false);
    const [blockModalClose, setBlockModalClose] = useState(false);
    const isDirty = useDirtyData(original, current);
    const [isValid, errors, validate] = useValidation();

    useEffect(loadDetails, [issuingAuthorityDetails, showModal]);

    function loadDetails() {
        if (!showModal) {
            return;
        }

        const mapped = {
            id: issuingAuthorityDetails.id || defaultModel.id,
            issuingAuthorityCode: issuingAuthorityDetails.issuingAuthorityCode || defaultModel.issuingAuthorityCode,
            name: issuingAuthorityDetails.name || defaultModel.name,
            appealWebsite: issuingAuthorityDetails.appealWebsite || defaultModel.appealWebsite,
            paymentWebsite: issuingAuthorityDetails.paymentWebsite || defaultModel.paymentWebsite,
            detailsWebsite: issuingAuthorityDetails.detailsWebsite || defaultModel.detailsWebsite,
            homeWebsite: issuingAuthorityDetails.homeWebsite || defaultModel.homeWebsite
        };

        setOriginal(mapped);
        setCurrent(mapped);
    }

    function onFieldChange(fieldName, passedValue) {
        setCurrent({
            ...current,
            [fieldName]: passedValue
        });
    }

    useEffect(runValidation, [current, showModal, loading]);

    function runValidation() {
        if (!showModal || loading) {
            return;
        }

        const rules = [
            { fieldName: "issuingAuthorityCode", required: true, maxLength: issuingAuthorityCodeMaxLength },
            { fieldName: "name", required: true, maxLength: nameMaxLength },
            { fieldName: "appealWebsite", maxLength: websiteMaxLength, type: 'url' },
            { fieldName: "paymentWebsite", maxLength: websiteMaxLength, type: 'url' },
            { fieldName: "detailsWebsite", maxLength: websiteMaxLength, type: 'url' },
            { fieldName: "homeWebsite", maxLength: websiteMaxLength, type: 'url' }
        ];

        validate(rules, current);
    }

    function onSaveClick() {
        if (!isValid || !isDirty || saving || loading) {
            return;
        }

        const request = {
            issuingAuthorityCode: current.issuingAuthorityCode,
            name: current.name,
            appealWebsite: current.appealWebsite,
            paymentWebsite: current.paymentWebsite,
            detailsWebsite: current.detailsWebsite,
            homeWebsite: current.homeWebsite
        };

        setSaving(true);

        const savePromise = isUpdate
            ? PcnService.updateIssuingAuthority(current.id, request)
            : PcnService.createIssuingAuthority(request);

        savePromise
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, isUpdate ? "Issuing authority updated successfully." : "Issuing authority created successfully.");
                onSaveComplete();
            })
            .catch((error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Could not save the issuing authority. Please try again.");
                }
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function onSaveComplete() {
        resetModal();
        onSave();
    }

    function onCloseClick(forceClose = false) {
        if (isDirty && !forceClose) {
            setBlockModalClose(true);
            return;
        }

        resetModal();

        if (onCloseModal) {
            onCloseModal();
        }
    }

    function resetModal() {
        setBlockModalClose(false);
        setOriginal(defaultModel);
        setCurrent(defaultModel);
    }

    return (
        <Fragment>
            <Modal
                isOpen={showModal}
                onClose={() => onCloseClick(false)}
                clickOutsideClose={true}
                title={modalTitle}
                size={ModalSize.MEDIUM}
                updateStateOnClose={false}
                footer={(
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            text="Cancel"
                            intent={Intent.SECONDARY}
                            onClick={() => onCloseClick(true)}
                        />
                        <ButtonSave
                            id="save-issuing-authority-button"
                            onClick={onSaveClick}
                            simpleDisabled={!isValid || loading || !isDirty}
                            disabled={saving}
                            text={"Save"}
                        />
                    </div>
                )}
            >
                <FormTextInput
                    id="input-issuing-authority-code"
                    disabled={saving}
                    loading={loading}
                    value={current.issuingAuthorityCode}
                    headingText="Issuing Authority Code"
                    placeholder="Please type the issuing authority code here"
                    onChange={(event) => onFieldChange("issuingAuthorityCode", event.target.value)}
                    dangerHelperText={errors.issuingAuthorityCode}
                    maxLength={issuingAuthorityCodeMaxLength}
                    large={true}
                    fill={true}
                />

                <FormTextInput
                    id="input-issuing-authority-name"
                    disabled={saving}
                    loading={loading}
                    value={current.name}
                    headingText="Name"
                    placeholder="Please type the name here"
                    onChange={(event) => onFieldChange("name", event.target.value)}
                    dangerHelperText={errors.name}
                    maxLength={nameMaxLength}
                    large={true}
                    fill={true}
                />

                <FormTextInput
                    id="input-issuing-authority-appeal-website"
                    disabled={saving}
                    loading={loading}
                    value={current.appealWebsite}
                    headingText="Appeal Website"
                    placeholder="Please type the appeal website here"
                    onChange={(event) => onFieldChange("appealWebsite", event.target.value)}
                    dangerHelperText={errors.appealWebsite}
                    maxLength={websiteMaxLength}
                    large={true}
                    fill={true}
                />

                <FormTextInput
                    id="input-issuing-authority-payment-website"
                    disabled={saving}
                    loading={loading}
                    value={current.paymentWebsite}
                    headingText="Payment Website"
                    placeholder="Please type the payment website here"
                    onChange={(event) => onFieldChange("paymentWebsite", event.target.value)}
                    dangerHelperText={errors.paymentWebsite}
                    maxLength={websiteMaxLength}
                    large={true}
                    fill={true}
                />

                <FormTextInput
                    id="input-issuing-authority-details-website"
                    disabled={saving}
                    loading={loading}
                    value={current.detailsWebsite}
                    headingText="Details Website"
                    placeholder="Please type the details website here"
                    onChange={(event) => onFieldChange("detailsWebsite", event.target.value)}
                    dangerHelperText={errors.detailsWebsite}
                    maxLength={websiteMaxLength}
                    large={true}
                    fill={true}
                />

                <FormTextInput
                    id="input-issuing-authority-home-website"
                    disabled={saving}
                    loading={loading}
                    value={current.homeWebsite}
                    headingText="Home Website"
                    placeholder="Please type the home website here"
                    onChange={(event) => onFieldChange("homeWebsite", event.target.value)}
                    dangerHelperText={errors.homeWebsite}
                    maxLength={websiteMaxLength}
                    large={true}
                    fill={true}
                />
            </Modal>

            <AlertUnsavedData
                isDirty={isDirty}
                isDirtySamePage={blockModalClose}
                onCancelLeave={() => setBlockModalClose(false)}
                onConfirmLeave={() => onCloseClick(true)}
            />
        </Fragment>
    );
}

PcnIssuingAuthorityModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    modalTitle: PropTypes.string.isRequired,
    isUpdate: PropTypes.bool,
    loading: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    issuingAuthorityDetails: PropTypes.object
};
